/**
 * Docs › Skills (ADR-216 §2.4): the skills people wrote and agents published
 * with the MCP tool publish_skill. The list shows each skill's latest version;
 * a skill's page shows every file before anyone installs it, the version
 * history, and three ways to install:
 *
 *  - Install: copies `npx -y @systemdox/mcp-server install-skill <name>`. A
 *    browser cannot write into a computer's home folder, so the button copies
 *    the command and the terminal does the install, with no model involved.
 *  - Copy for Claude: copies the sentence for a Claude session that has the
 *    SystemDox MCP server connected.
 *  - Download .zip: the version's folder, zipped here, for claude.ai and
 *    Claude Desktop (Settings › Capabilities › Skills).
 *
 * Amendment 1 (2026-11-01): each file is shown by its type (SkillFileView),
 * any kept version opens and compares with another (SkillCompare), a member
 * with build:write edits the skill here and publishes the next version
 * (SkillEditor), and an older version can be restored as the next one.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Bot,
  Check,
  Copy,
  Download,
  FileText,
  GitCompare,
  Package,
  Pencil,
  RotateCcw,
  Terminal,
  Trash2,
} from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import {
  skillsApi,
  type SkillDetail,
  type SkillSummary,
  type SkillVersionFiles,
} from '@/services/skillsApi';
import { apiKeysApi } from '@/services/apiKeysApi';
import { tenantApi } from '@/services/tenantApi';
import { useTenant } from '@/contexts/TenantContext';
import { hasSpaceScope } from '@/lib/grants';
import { createZip } from '@/lib/zip';
import { cn } from '@/lib/utils';
import { copyForClaudeText, installSkillCommand } from './skillInstall';
import { SkillFileView } from './SkillFileView';
import { refusalText } from './SkillEditor';

function day(iso: string): string {
  return iso ? iso.slice(0, 10) : '';
}

function kb(bytes: number): string {
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;
}

/**
 * Team setup's switch on each skill (ADR-217 §2.4): on, the skill is in the
 * team plugin every member installs. Given by Docs › Team setup; absent where
 * the list is shown on its own.
 */
export interface SetupSwitch {
  inSetup: (name: string) => boolean;
  toggle: (name: string, on: boolean) => void;
  busy: boolean;
}

export default function SkillsTab({ setup }: { setup?: SetupSwitch } = {}) {
  const [skills, setSkills] = useState<SkillSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    skillsApi
      .list()
      .then(setSkills)
      .catch(() => setError('Failed to load skills.'));
  }, []);

  if (error) return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (!skills) return <LoadingState />;

  return (
    <div className="mx-auto max-w-4xl">
      <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
        Claude Code skills your team wrote, published by an agent with the MCP tool{' '}
        <code className="font-mono text-xs">publish_skill</code>. Install one on any computer from its page.
      </p>
      {skills.length === 0 ? (
        <div className="py-12 text-center" data-testid="skills-empty">
          <Package className="mx-auto mb-4 h-12 w-12 text-slate-300 dark:text-slate-700" />
          <p className="text-sm text-slate-500">No skills published yet.</p>
          <p className="mt-1 text-xs text-slate-400">
            Ask a Claude session with the SystemDox MCP server: “Publish my &lt;name&gt; skill to SystemDox.”
          </p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="skills-list">
          {skills.map((s) => (
            <li key={s.name} className="flex items-stretch gap-2">
              <Link
                to={`/docs/skills/${encodeURIComponent(s.name)}`}
                className="block min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-4 py-3 transition-colors hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-600"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-mono text-sm font-medium text-slate-900 dark:text-slate-100">{s.name}</span>
                  <span className="shrink-0 text-xs text-slate-500">
                    version {s.latestVersion} · {day(s.updatedAt)}
                  </span>
                </div>
                <p className="mt-1 line-clamp-2 text-xs text-slate-600 dark:text-slate-400">{s.description}</p>
              </Link>
              {setup && <SetupToggle name={s.name} setup={setup} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SetupToggle({ name, setup }: { name: string; setup: SetupSwitch }) {
  const on = setup.inSetup(name);
  return (
    <label
      className="flex shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300"
      data-testid={`skill-setup-${name}`}
    >
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={`In the team setup: ${name}`}
        disabled={setup.busy}
        onClick={() => setup.toggle(name, !on)}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50',
          on ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-700',
        )}
      >
        <span
          className={cn(
            'inline-block h-4 w-4 rounded-full bg-white shadow transition-transform',
            on ? 'translate-x-4' : 'translate-x-0.5',
          )}
        />
      </button>
      In the team setup
    </label>
  );
}

export function CopyButton({
  label,
  text,
  icon: Icon,
  primary = false,
  testId,
}: {
  label: string;
  text: string;
  icon: typeof Copy;
  primary?: boolean;
  testId: string;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testId}
      className={cn(
        'flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium',
        primary
          ? 'bg-blue-600 text-white hover:bg-blue-700'
          : 'border border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800',
      )}
    >
      {copied ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
      {copied ? 'Copied' : label}
    </button>
  );
}

/**
 * Names for "published by": members by user id and, for versions published
 * with an API key before the publisher was recorded as the key's owner, the
 * key by its name. Lists the caller may not read fall back to "a member".
 * With `addresses: false` a member with no name is "a member" too, never
 * their email address (Team setup's Memory tab shows no address).
 */
export function usePublisherNames({ addresses = true }: { addresses?: boolean } = {}): (userId?: string) => string {
  const { tenant } = useTenant();
  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!tenant?.tenantId) return;
    let live = true;
    Promise.all([
      tenantApi.listMembers(tenant.tenantId).catch(() => []),
      apiKeysApi.list().catch(() => []),
    ]).then(([members, keys]) => {
      if (!live) return;
      setNames({
        ...Object.fromEntries(keys.map((k) => [k.keyId, `API key “${k.name}”`])),
        ...Object.fromEntries(
          members
            .map((m) => [m.userId.replace(/^usr_/, ''), m.name || (addresses ? m.email : '')])
            .filter(([, label]) => label),
        ),
      });
    });
    return () => {
      live = false;
    };
  }, [tenant?.tenantId, addresses]);
  return (userId?: string) => (userId && (names[userId.replace(/^usr_/, '')] ?? names[userId])) || 'a member';
}

export function SkillDetailPage() {
  const { name = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { tenant } = useTenant();
  const nameOf = usePublisherNames();
  const [skill, setSkill] = useState<SkillDetail | null>(null);
  const [files, setFiles] = useState<SkillVersionFiles | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const asked = Number(params.get('version')) || undefined;
  const selected = params.get('file') ?? 'SKILL.md';
  const published = (location.state as { published?: number } | null)?.published;

  useEffect(() => {
    Promise.all([skillsApi.get(name), skillsApi.version(name, asked ?? 'latest')])
      .then(([detail, shown]) => {
        setSkill(detail);
        setFiles(shown);
      })
      .catch(() =>
        setError(
          asked
            ? `Version ${asked} of this skill is not kept (the newest 20 are), or you cannot read its space.`
            : 'This skill does not exist, or you cannot read its space.',
        ),
      );
  }, [name, asked]);

  const shown = useMemo(
    () => files?.files.find((f) => f.path === selected) ?? files?.files[0],
    [files, selected],
  );
  const paths = useMemo(() => files?.files.map((f) => f.path) ?? [], [files]);

  function show(change: { version?: number | null; file?: string | null }) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(change)) {
      if (value === undefined) continue;
      if (value === null) next.delete(key);
      else next.set(key, String(value));
    }
    setParams(next);
  }

  function downloadZip() {
    if (!files) return;
    const blob = createZip(files.files.map((f) => ({ path: `${files.name}/${f.path}`, content: f.content })));
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${files.name}-v${files.version}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function remove() {
    try {
      await skillsApi.remove(name);
      navigate('/docs/skills');
    } catch {
      setConfirmDelete(false);
      setError('Could not delete the skill: deleting needs write access to its space.');
    }
  }

  /** History is append-only: an older version comes back as the next one. */
  async function restore() {
    if (!files || !skill) return;
    setRestoring(true);
    try {
      const out = await skillsApi.publish(name, {
        files: files.files.map(({ path, content }) => ({ path, content })),
        note: `Restored from version ${files.version}`,
        baseVersion: skill.latestVersion,
      });
      navigate(`/docs/skills/${encodeURIComponent(name)}`, { state: { published: out.version } });
    } catch (err) {
      setError(`Could not restore version ${files.version}: ${refusalText(err).text}`);
    } finally {
      setRestoring(false);
    }
  }

  if (error && !skill) return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (!skill || !files) return <LoadingState />;

  const latest = files.version === skill.latestVersion;
  const pinned = latest ? undefined : files.version;
  const canWrite = hasSpaceScope(tenant?.grants, skill.spaceId, 'build:write');
  const previous = (v: number) => skill.versions.find((o) => o.version < v)?.version;
  const base = `/docs/skills/${encodeURIComponent(name)}`;
  const fileLink = (path: string) =>
    `${window.location.origin}${base}?version=${files.version}&file=${encodeURIComponent(path)}`;

  return (
    <div className="mx-auto max-w-5xl" data-testid="skill-detail">
      <Link to="/docs/skills" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-4 w-4" /> Skills
      </Link>
      <div className="mb-4">
        <h2 className="font-mono text-lg font-semibold text-slate-900 dark:text-slate-100">{skill.name}</h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{skill.description}</p>
        <p className="mt-1 text-xs text-slate-500">
          version {files.version} · published {day(files.publishedAt)} by {nameOf(files.publishedBy)}
        </p>
      </div>

      {published && latest && (
        <p className="mb-4 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200" data-testid="skill-published">
          Published version {published}. Computers that installed this skill get it when Install&apos;s command runs
          there again.
        </p>
      )}

      {!latest && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200" data-testid="skill-old-version">
          <span>
            Version {files.version} of {skill.latestVersion}. Not the latest.
          </span>
          <button type="button" onClick={() => show({ version: null })} className="underline">
            Open the latest
          </button>
          <Link to={`${base}/compare?from=${files.version}&to=${skill.latestVersion}`} className="underline">
            Compare with the latest
          </Link>
          {canWrite && (
            <button
              type="button"
              onClick={restore}
              disabled={restoring}
              data-testid="skill-restore"
              className="ml-auto flex items-center gap-1.5 rounded-md border border-amber-400 px-3 py-1.5 text-sm font-medium hover:bg-amber-100 disabled:opacity-50 dark:hover:bg-amber-900/40"
            >
              <RotateCcw className="h-4 w-4" /> Restore as version {skill.latestVersion + 1}
            </button>
          )}
        </div>
      )}

      <div className="mb-2 flex flex-wrap gap-2">
        <CopyButton
          label="Install"
          text={installSkillCommand(skill.name, pinned)}
          icon={Terminal}
          primary
          testId="skill-install"
        />
        <CopyButton label="Copy for Claude" text={copyForClaudeText(skill.name)} icon={Bot} testId="skill-copy-claude" />
        <button
          type="button"
          onClick={downloadZip}
          data-testid="skill-download"
          className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <Download className="h-4 w-4" /> Download .zip
        </button>
        {skill.versions.length > 1 && (
          <Link
            to={`${base}/compare?from=${previous(files.version) ?? files.version}&to=${files.version}`}
            data-testid="skill-compare"
            className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <GitCompare className="h-4 w-4" /> Compare
          </Link>
        )}
        {canWrite && latest && (
          <Link
            to={`${base}/edit`}
            data-testid="skill-edit"
            className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <Pencil className="h-4 w-4" /> Edit
          </Link>
        )}
        <div className="ml-auto">
          {confirmDelete ? (
            <button
              type="button"
              onClick={remove}
              data-testid="skill-delete-confirm"
              className="flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4" /> Delete every version
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              data-testid="skill-delete"
              className="flex items-center gap-1.5 rounded-md px-3 py-2 text-sm text-slate-500 hover:text-red-600"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          )}
        </div>
      </div>
      <p className="mb-6 text-xs text-slate-500" data-testid="skill-install-hint">
        Install copies <code className="font-mono">{installSkillCommand(skill.name, pinned)}</code>: paste it into a
        terminal on the computer where you use Claude Code (it needs{' '}
        <code className="font-mono">SYSTEMDOX_API_KEY</code>). It writes into{' '}
        <code className="font-mono">~/.claude/skills/{skill.name}/</code> and never replaces a file you edited there.
      </p>
      {error && <p className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</p>}

      <div className="grid gap-4 md:grid-cols-[14rem_1fr]">
        <ul className="space-y-0.5 text-sm" data-testid="skill-files">
          {files.files.map((f) => (
            <li key={f.path}>
              <button
                type="button"
                onClick={() => show({ file: f.path })}
                className={cn(
                  'flex w-full items-center gap-1.5 rounded px-2 py-1 text-left font-mono text-xs',
                  f.path === shown?.path
                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800',
                )}
              >
                <FileText className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{f.path}</span>
                <span className="ml-auto shrink-0 text-[10px] text-slate-400">{kb(f.bytes)}</span>
              </button>
            </li>
          ))}
        </ul>
        {shown && (
          <SkillFileView
            key={`${files.version}/${shown.path}`}
            file={shown}
            paths={paths}
            onOpen={(path) => show({ file: path })}
            link={fileLink(shown.path)}
          />
        )}
      </div>

      <h3 className="mt-8 mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">Versions</h3>
      <ul className="divide-y divide-slate-200 text-xs dark:divide-slate-800" data-testid="skill-versions">
        {skill.versions.map((v) => (
          <li
            key={v.version}
            className={cn(
              'flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-1.5 text-slate-600 dark:text-slate-400',
              v.version === files.version && 'font-medium text-slate-900 dark:text-slate-100',
            )}
          >
            <button
              type="button"
              onClick={() => show({ version: v.version === skill.latestVersion ? null : v.version })}
              data-testid={`skill-open-version-${v.version}`}
              className="hover:underline"
            >
              version {v.version} · {v.fileCount} file(s), {kb(v.totalBytes)}
            </button>
            {v.note && <span className="text-slate-500 italic">{v.note}</span>}
            <span className="ml-auto">
              {day(v.publishedAt)} by {nameOf(v.publishedBy)}
            </span>
            {previous(v.version) && (
              <Link
                to={`${base}/compare?from=${previous(v.version)}&to=${v.version}`}
                data-testid={`skill-compare-previous-${v.version}`}
                className="text-blue-600 hover:underline dark:text-blue-400"
              >
                Compare with previous
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
